/**
 * Yamaga トップバナー — Swiper.js 初期化
 */
(function () {
  const ROOT_SELECTOR = '.yamaga-slideshow';
  const DESKTOP_MEDIA = '(min-width: 750px)';
  const DEFAULT_SLIDE_WIDTH_PX = 400;
  const resizeObservers = new WeakMap();

  function remToPx(rem) {
    const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    return rem * rootFontSize;
  }

  function isDesktop() {
    return window.matchMedia(DESKTOP_MEDIA).matches;
  }

  function getDefaultSlideWidth() {
    return Math.min(DEFAULT_SLIDE_WIDTH_PX, window.innerWidth - remToPx(3));
  }

  function getSlideGap(root) {
    const gap = getComputedStyle(root).getPropertyValue('--yamaga-slideshow-gap').trim();
    const match = gap.match(/^([\d.]+)rem$/);
    return match ? remToPx(parseFloat(match[1])) : remToPx(2.4);
  }

  function getLayoutMode(root, slideCount) {
    const stage = root.querySelector('.yamaga-slideshow__stage');
    const containerWidth = stage ? stage.offsetWidth : root.offsetWidth;
    const gap = getSlideGap(root);
    const defaultWidth = getDefaultSlideWidth();
    const visibleAtDefault = Math.max(1, Math.floor((containerWidth + gap) / (defaultWidth + gap)));

    if (slideCount <= 1) {
      return {
        fill: false,
        centeredSlides: true,
        slideWidth: Math.min(defaultWidth, containerWidth),
        canLoop: false,
      };
    }

    if (isDesktop() && slideCount <= visibleAtDefault) {
      return {
        fill: true,
        centeredSlides: false,
        slideWidth: (containerWidth - (slideCount - 1) * gap) / slideCount,
        canLoop: true,
      };
    }

    return {
      fill: false,
      centeredSlides: true,
      slideWidth: defaultWidth,
      canLoop: true,
    };
  }

  function applyLayoutMode(root, slideCount) {
    const layout = getLayoutMode(root, slideCount);
    root.classList.toggle('yamaga-slideshow--fill', layout.fill);
    root.style.setProperty('--yamaga-slideshow-slide-width', `${layout.slideWidth}px`);
    return layout;
  }

  function prepareInfiniteSlides(swiperEl) {
    const wrapper = swiperEl.querySelector('.swiper-wrapper');
    if (!wrapper) return { originalCount: 0, startIndex: 0 };

    wrapper.querySelectorAll('.yamaga-slideshow-clone').forEach((clone) => clone.remove());

    const originals = [...wrapper.querySelectorAll('.swiper-slide')];
    if (originals.length < 2) {
      return { originalCount: originals.length, startIndex: 0 };
    }

    originals.forEach((slide) => {
      const clone = slide.cloneNode(true);
      clone.classList.add('yamaga-slideshow-clone');
      clone.removeAttribute('id');
      clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
      wrapper.appendChild(clone);
    });

    [...originals].reverse().forEach((slide) => {
      const clone = slide.cloneNode(true);
      clone.classList.add('yamaga-slideshow-clone');
      clone.removeAttribute('id');
      clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
      wrapper.insertBefore(clone, wrapper.firstChild);
    });

    return { originalCount: originals.length, startIndex: originals.length };
  }

  function cleanupInfiniteSlides(swiperEl) {
    swiperEl
      .querySelectorAll('.swiper-slide.yamaga-slideshow-clone')
      .forEach((clone) => clone.remove());
  }

  function getRealIndex(activeIndex, originalCount) {
    if (!originalCount) return 0;
    return ((activeIndex % originalCount) + originalCount) % originalCount;
  }

  function bindInfiniteLoopFix(swiper, originalCount) {
    if (originalCount < 2) return;

    swiper.on('slideChangeTransitionEnd', () => {
      const index = swiper.activeIndex;
      if (index >= originalCount * 2) {
        swiper.slideTo(index - originalCount, 0, false);
      } else if (index < originalCount) {
        swiper.slideTo(index + originalCount, 0, false);
      }
    });
  }

  function initPagination(root, swiper, originalCount, paginationEl) {
    if (!paginationEl || originalCount < 2) return;

    const visual = root.dataset.sliderVisual || 'dots';
    paginationEl.innerHTML = '';
    paginationEl.classList.toggle('yamaga-slideshow__pagination--counter', visual === 'counter');

    const syncActiveBullet = () => {
      const realIndex = getRealIndex(swiper.activeIndex, originalCount);

      if (visual === 'counter') {
        paginationEl.textContent = `${realIndex + 1} / ${originalCount}`;
        return;
      }

      paginationEl.querySelectorAll('.swiper-pagination-bullet').forEach((bullet, index) => {
        bullet.classList.toggle('swiper-pagination-bullet-active', index === realIndex);
        bullet.setAttribute('aria-current', index === realIndex ? 'true' : 'false');
      });
    };

    if (visual === 'counter') {
      syncActiveBullet();
      swiper.on('slideChange', syncActiveBullet);
      return;
    }

    for (let index = 0; index < originalCount; index += 1) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'swiper-pagination-bullet';
      button.setAttribute('aria-label', `Slide ${index + 1}`);

      if (visual === 'numbers') {
        button.classList.add('yamaga-slideshow__bullet-number');
        button.textContent = String(index + 1);
      } else {
        button.classList.add('yamaga-slideshow__bullet-dot');
        button.innerHTML = '<span class="dot" aria-hidden="true"></span>';
      }

      button.addEventListener('click', () => {
        swiper.slideTo(originalCount + index);
      });

      paginationEl.appendChild(button);
    }

    swiper.on('slideChange', syncActiveBullet);
    syncActiveBullet();
  }

  function shouldAutoplay(root, canLoop) {
    return canLoop && root.dataset.autoplay === 'true' && !window.Shopify?.designMode;
  }

  function startAutoplay(swiper) {
    if (!swiper.autoplay) return;
    swiper.autoplay.start();
  }

  function bindAutoplayToggle(root, swiper) {
    const toggle = root.querySelector('.yamaga-slideshow__autoplay');
    if (!toggle || !swiper.autoplay) return;

    if (toggle.dataset.bound === 'true') return;
    toggle.dataset.bound = 'true';

    toggle.addEventListener('click', () => {
      if (swiper.autoplay.running) {
        swiper.autoplay.stop();
        toggle.classList.add('is-paused');
        toggle.setAttribute('aria-label', toggle.dataset.playLabel || 'Play slideshow');
      } else {
        swiper.autoplay.start();
        toggle.classList.remove('is-paused');
        toggle.setAttribute('aria-label', toggle.dataset.pauseLabel || 'Pause slideshow');
      }
    });
  }

  function initYamagaSlideshow(root) {
    if (typeof Swiper === 'undefined') return;

    if (root.swiperInstance) {
      root.swiperInstance.destroy(true, true);
      root.swiperInstance = null;
    }

    const swiperEl = root.querySelector('.yamaga-slideshow__swiper');
    if (!swiperEl) return;

    cleanupInfiniteSlides(swiperEl);

    const slides = swiperEl.querySelectorAll('.swiper-slide');
    if (!slides.length) return;

    const paginationEl = root.querySelector('.yamaga-slideshow__pagination');
    const prevEl = root.querySelector('.yamaga-slideshow__nav--prev');
    const nextEl = root.querySelector('.yamaga-slideshow__nav--next');
    const slideCount = slides.length;
    const layout = applyLayoutMode(root, slideCount);
    const canLoop = layout.canLoop;
    const enableAutoplay = shouldAutoplay(root, canLoop);
    const infinite = prepareInfiniteSlides(swiperEl);

    const swiper = new Swiper(swiperEl, {
      loop: false,
      initialSlide: infinite.startIndex,
      slidesPerView: 'auto',
      slidesPerGroup: 1,
      centeredSlides: layout.centeredSlides,
      spaceBetween: getSlideGap(root),
      speed: 400,
      watchOverflow: !enableAutoplay,
      navigation: canLoop
        ? {
            prevEl,
            nextEl,
          }
        : false,
      pagination: false,
      autoplay: enableAutoplay
        ? {
            delay: (parseInt(root.dataset.speed, 10) || 5) * 1000,
            disableOnInteraction: false,
            pauseOnMouseEnter: false,
          }
        : false,
      a11y: {
        enabled: true,
        prevSlideMessage: 'Previous slide',
        nextSlideMessage: 'Next slide',
      },
      on: {
        init(swiperInstance) {
          if (infinite.startIndex) {
            swiperInstance.slideTo(infinite.startIndex, 0, false);
          }
          initPagination(root, swiperInstance, infinite.originalCount, paginationEl);
          startAutoplay(swiperInstance);
        },
      },
    });

    bindInfiniteLoopFix(swiper, infinite.originalCount);

    root.swiperInstance = swiper;
    bindAutoplayToggle(root, swiper);
    bindResizeObserver(root);
  }

  function bindResizeObserver(root) {
    if (resizeObservers.has(root)) return;

    const stage = root.querySelector('.yamaga-slideshow__stage') || root;
    let resizeTimer;
    let lastWidth = stage.offsetWidth;

    const observer = new ResizeObserver(() => {
      if (root.swiperInstance?.animating) return;

      const nextWidth = stage.offsetWidth;
      if (Math.abs(nextWidth - lastWidth) < 1) return;
      lastWidth = nextWidth;

      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        if (root.swiperInstance?.animating) return;
        initYamagaSlideshow(root);
      }, 150);
    });

    observer.observe(stage);
    resizeObservers.set(root, observer);
  }

  function initAll(scope) {
    (scope || document).querySelectorAll(ROOT_SELECTOR).forEach(initYamagaSlideshow);
  }

  function destroyAll(scope) {
    (scope || document).querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      const observer = resizeObservers.get(root);
      if (observer) {
        observer.disconnect();
        resizeObservers.delete(root);
      }

      const toggle = root.querySelector('.yamaga-slideshow__autoplay');
      if (toggle) {
        delete toggle.dataset.bound;
      }

      if (root.swiperInstance) {
        root.swiperInstance.destroy(true, true);
        root.swiperInstance = null;
      }

      const swiperEl = root.querySelector('.yamaga-slideshow__swiper');
      if (swiperEl) {
        cleanupInfiniteSlides(swiperEl);
      }
    });
  }

  function onReady() {
    initAll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroyAll(event.target);
  });
})();
